import type {
  DecodedBrowserAppointment,
  ParticipantAppointment,
  ParticipantAppointmentDetail,
} from '@/features/appointments/types';
import type { BookAppointmentRequestV3 } from '@/store/api/appointmentApi';
import type {
  BookMomentsParticipantAppointmentRequest,
  ParticipantAppointmentTransport,
} from '@/store/api/momentsParticipantAppointmentApi';

const STORAGE_PREFIX = 'member-single-booking:attempt:';

export interface StoredSingleBookingAttempt {
  command:
    | (BookAppointmentRequestV3 & {
        browserContractMarker: 'browser-appointments.booking-pattern-v3';
        idempotencyKey: string;
      })
    | BookMomentsParticipantAppointmentRequest;
  confirmation: {
    endAt: string;
    startAt: string;
  } | null;
  confirmationInvalidated: boolean;
  identity: {
    coachPersonId: string;
    memberPersonId: string;
    seatType: 'executive' | 'leadership';
  };
  operationId: string | null;
  ownerKey: string;
  presentation: {
    coachAvatarUrl: string | null;
    coachDisplayName: string;
    durationMinutes: number;
    memberEmail: string;
    timezone: string;
  };
  status:
    | 'confirmed'
    | 'error'
    | 'needs_reconciliation'
    | 'pending'
    | 'retry'
    | 'submission_unknown'
    | 'submitting';
  transport?: ParticipantAppointmentTransport;
  version: 1;
}

export const singleBookingAttemptKey = (ownerKey: string) =>
  `${STORAGE_PREFIX}${encodeURIComponent(ownerKey)}`;

const isAttempt = (value: unknown): value is StoredSingleBookingAttempt => {
  if (!value || typeof value !== 'object') return false;
  const attempt = value as Partial<StoredSingleBookingAttempt>;
  const command = attempt.command as unknown as Record<string, unknown>;
  const bookingPattern = command.bookingPattern as
    Record<string, unknown> | undefined;
  const counterpart = command.counterpart as
    Record<string, unknown> | null | undefined;
  const confirmation = attempt.confirmation as
    | Partial<NonNullable<StoredSingleBookingAttempt['confirmation']>>
    | null
    | undefined;
  const identity = attempt.identity as
    Partial<StoredSingleBookingAttempt['identity']> | undefined;
  const presentation = attempt.presentation as
    Partial<StoredSingleBookingAttempt['presentation']> | undefined;
  const { transport: rawTransport } = value as { transport?: unknown };
  const transport = rawTransport ?? 'rails';
  const validCommand =
    transport === 'moments'
      ? command.bookingPattern === 'one_off' &&
        command.kind === 'coaching' &&
        typeof counterpart === 'object' &&
        counterpart !== null &&
        typeof counterpart.externalId === 'string' &&
        counterpart.externalId.length > 0 &&
        counterpart.provider === 'legacy_app' &&
        typeof command.idempotencyKey === 'string' &&
        typeof command.startAt === 'string' &&
        typeof command.viewerTimezone === 'string'
      : command.contractVersion === 'appointment-book.v3' &&
        command.browserContractMarker ===
          'browser-appointments.booking-pattern-v3' &&
        bookingPattern?.type === 'one_off' &&
        command.kind === 'coaching' &&
        typeof command.coachId === 'string' &&
        typeof command.expectedCalendarEventTitle === 'string' &&
        typeof command.idempotencyKey === 'string' &&
        typeof command.startAt === 'string' &&
        typeof command.viewerTimezone === 'string';
  const structurallyValid =
    attempt.version === 1 &&
    (rawTransport === undefined ||
      rawTransport === 'rails' ||
      rawTransport === 'moments') &&
    typeof attempt.ownerKey === 'string' &&
    attempt.ownerKey.length > 0 &&
    [
      'confirmed',
      'error',
      'needs_reconciliation',
      'pending',
      'submitting',
    ].includes(attempt.status ?? '') &&
    (attempt.operationId === null || typeof attempt.operationId === 'string') &&
    validCommand &&
    typeof attempt.confirmationInvalidated === 'boolean' &&
    (confirmation === null ||
      (confirmation !== undefined &&
        typeof confirmation.startAt === 'string' &&
        Number.isFinite(Date.parse(confirmation.startAt)) &&
        typeof confirmation.endAt === 'string' &&
        Number.isFinite(Date.parse(confirmation.endAt)) &&
        Date.parse(confirmation.endAt) > Date.parse(confirmation.startAt))) &&
    identity !== undefined &&
    typeof identity.coachPersonId === 'string' &&
    identity.coachPersonId.length > 0 &&
    typeof identity.memberPersonId === 'string' &&
    identity.memberPersonId.length > 0 &&
    (identity.seatType === 'executive' || identity.seatType === 'leadership') &&
    presentation !== undefined &&
    (presentation.coachAvatarUrl === null ||
      typeof presentation.coachAvatarUrl === 'string') &&
    typeof presentation.coachDisplayName === 'string' &&
    presentation.coachDisplayName.length > 0 &&
    typeof presentation.durationMinutes === 'number' &&
    presentation.durationMinutes > 0 &&
    typeof presentation.memberEmail === 'string' &&
    presentation.memberEmail.length > 0 &&
    typeof presentation.timezone === 'string' &&
    presentation.timezone.length > 0;
  if (!structurallyValid) return false;
  if (attempt.status === 'confirmed') {
    return Boolean(attempt.operationId) && confirmation !== null;
  }
  if (
    attempt.status === 'pending' ||
    attempt.status === 'needs_reconciliation'
  ) {
    return (
      Boolean(attempt.operationId) &&
      confirmation === null &&
      attempt.confirmationInvalidated === false
    );
  }
  return (
    attempt.operationId === null &&
    confirmation === null &&
    attempt.confirmationInvalidated === false
  );
};

export const saveSingleBookingAttempt = (
  attempt: StoredSingleBookingAttempt,
): boolean => {
  try {
    if (!isAttempt(attempt)) return false;
    window.sessionStorage.setItem(
      singleBookingAttemptKey(attempt.ownerKey),
      JSON.stringify(attempt),
    );
    return true;
  } catch {
    return false;
  }
};

export const loadSingleBookingAttempt = (
  ownerKey: string,
): StoredSingleBookingAttempt | null => {
  try {
    const key = singleBookingAttemptKey(ownerKey);
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isAttempt(parsed) || parsed.ownerKey !== ownerKey) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed.status === 'submitting'
      ? { ...parsed, status: 'submission_unknown' }
      : parsed;
  } catch {
    return null;
  }
};

export const clearSingleBookingAttempt = (ownerKey: string): void => {
  try {
    window.sessionStorage.removeItem(singleBookingAttemptKey(ownerKey));
  } catch {
    // Navigation remains available if browser storage is unavailable.
  }
};

export const confirmedSingleAppointment = ({
  coachPersonId,
  memberPersonId,
  result,
  seatType,
  startAt,
}: {
  coachPersonId: string;
  memberPersonId: string;
  result: {
    appointments: (
      | DecodedBrowserAppointment
      | ParticipantAppointment
      | ParticipantAppointmentDetail
    )[];
    operationId: string;
    status: string;
  };
  seatType: 'executive' | 'leadership';
  startAt: string;
}) => {
  if (result.status !== 'confirmed' || result.appointments.length !== 1)
    return null;
  const appointment = result.appointments[0]!;
  return appointment.kind === 'coaching' &&
    appointment.seriesId === null &&
    appointment.seriesMutationVersion === 0 &&
    appointment.seatType === seatType &&
    appointment.expectedIdentities.coach.personId === coachPersonId &&
    appointment.expectedIdentities.member.personId === memberPersonId &&
    Date.parse(appointment.currentSchedule.startAt) === Date.parse(startAt)
    ? appointment
    : null;
};
